import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Package, Mail, FileText, LogOut, ShieldAlert, FileDown, Database } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ProductsAdmin from "@/components/admin/ProductsAdmin";
import MessagesAdmin from "@/components/admin/MessagesAdmin";
import QuotesAdmin from "@/components/admin/QuotesAdmin";
import DownloadsAdmin from "@/components/admin/DownloadsAdmin";
import CollaboratorsAdmin from "@/components/admin/CollaboratorsAdmin";
import AccountAdmin from "@/components/admin/AccountAdmin";

export default function Dashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (!user.is_editor) return;
    api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>;
  }

  if (!user) return null;

  if (!user.is_editor) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-16">
        <div className="max-w-md text-center bg-white border-2 border-red-600 p-10">
          <ShieldAlert className="w-12 h-12 mx-auto text-red-600" />
          <h2 className="font-display text-2xl uppercase tracking-tight mt-4">Acceso denegado</h2>
          <p className="text-slate-600 mt-3 text-sm">
            Tu cuenta no tiene permisos para acceder al panel. Pide al administrador que te agregue como colaborador.
          </p>
          <p className="font-mono text-xs text-slate-500 mt-2">{user.email}</p>
          <button onClick={async () => { await logout(); navigate("/login"); }} className="mt-6 bg-slate-950 text-white px-5 py-3 font-display font-bold uppercase tracking-wider text-xs" data-testid="unauth-logout-btn">
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="font-mono text-xs tracking-[0.3em] uppercase text-amber-500 mb-2">Panel administrador</div>
            <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight">
              Centro de control
            </h1>
            <p className="text-slate-400 mt-2 font-mono text-sm">{user.email}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
            {user.is_admin && (
              <button
                onClick={async () => {
                  try {
                    const res = await api.get("/admin/backup", { responseType: "blob" });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement("a");
                    a.href = url;
                    const today = new Date().toISOString().slice(0, 10);
                    a.download = `gwm-backup-${today}.json`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  } catch (e) {
                    // noop
                  }
                }}
                data-testid="dashboard-backup-btn"
                className="inline-flex items-center gap-2 bg-amber-500 text-slate-950 px-5 py-3 font-display font-bold uppercase tracking-wider text-xs hover:bg-amber-400 transition-colors"
                title="Descargar respaldo JSON de toda la base de datos"
              >
                <Database className="w-4 h-4" /> Respaldo
              </button>
            )}
            <button onClick={async () => { await logout(); navigate("/"); }} data-testid="dashboard-logout-btn" className="inline-flex items-center gap-2 bg-white text-slate-950 px-5 py-3 font-display font-bold uppercase tracking-wider text-xs hover:bg-slate-100 transition-colors">
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </button>
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 -mt-8 md:-mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Productos" value={stats.products} sub={`${stats.active_products} activos`} icon={Package} testId="stat-products" />
          <StatCard label="Mensajes" value={stats.contact_messages} sub={`${stats.unread_contact} sin leer`} icon={Mail} testId="stat-messages" highlight={stats.unread_contact > 0} />
          <StatCard label="Cotizaciones" value={stats.quotes} sub={`${stats.unread_quotes} nuevas`} icon={FileText} testId="stat-quotes" highlight={stats.unread_quotes > 0} />
          <StatCard label="Descargas PDF" value={stats.downloads ?? 0} sub="Fichas técnicas" icon={FileDown} testId="stat-downloads" />
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <Tabs defaultValue="products" className="w-full">
          <TabsList className="bg-white border-2 border-slate-950 rounded-none p-0 h-auto flex-wrap" data-testid="admin-tabs">
            <TabsTrigger value="account" className="rounded-none px-6 py-3 font-display font-bold uppercase text-xs tracking-wider">Mi cuenta</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products" className="rounded-none px-6 py-3 font-display font-bold uppercase text-xs tracking-wider data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950 data-[state=active]:shadow-none">
              Productos
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes" className="rounded-none px-6 py-3 font-display font-bold uppercase text-xs tracking-wider data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950 data-[state=active]:shadow-none">
              Cotizaciones {stats?.unread_quotes > 0 && <span className="ml-2 bg-red-600 text-white text-[10px] px-1.5 py-0.5">{stats.unread_quotes}</span>}
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-none px-6 py-3 font-display font-bold uppercase text-xs tracking-wider data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950 data-[state=active]:shadow-none">
              Mensajes {stats?.unread_contact > 0 && <span className="ml-2 bg-red-600 text-white text-[10px] px-1.5 py-0.5">{stats.unread_contact}</span>}
            </TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-downloads" className="rounded-none px-6 py-3 font-display font-bold uppercase text-xs tracking-wider data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950 data-[state=active]:shadow-none">
              Descargas
            </TabsTrigger>
            {user.is_admin && (
              <TabsTrigger value="collaborators" data-testid="tab-collaborators" className="rounded-none px-6 py-3 font-display font-bold uppercase text-xs tracking-wider data-[state=active]:bg-amber-500 data-[state=active]:text-slate-950 data-[state=active]:shadow-none">
                Colaboradores
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="products" className="mt-6"><ProductsAdmin onChange={() => api.get("/admin/stats").then((r)=>setStats(r.data))} /></TabsContent>
          <TabsContent value="quotes" className="mt-6"><QuotesAdmin onChange={() => api.get("/admin/stats").then((r)=>setStats(r.data))} /></TabsContent>
          <TabsContent value="messages" className="mt-6"><MessagesAdmin onChange={() => api.get("/admin/stats").then((r)=>setStats(r.data))} /></TabsContent>
          <TabsContent value="downloads" className="mt-6"><DownloadsAdmin /></TabsContent>
          <TabsContent value="account" className="mt-6"><AccountAdmin /></TabsContent>
          {user.is_admin && (
            <TabsContent value="collaborators" className="mt-6"><CollaboratorsAdmin /></TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, testId, highlight }) {
  return (
    <div data-testid={testId} className={`bg-white border-2 ${highlight ? "border-amber-500" : "border-slate-950"} p-5`}>
      <div className="flex items-start justify-between">
        <Icon className="w-5 h-5 text-amber-500" />
        {highlight && <span className="w-2 h-2 bg-red-600 rounded-full animate-pulse" />}
      </div>
      <div className="font-display font-black text-4xl mt-2 tracking-tight">{value}</div>
      <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      <div className="text-[11px] font-mono text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}
